import React from 'react';
import { Link } from 'react-router-dom';
import { Award, GraduationCap, BookOpen, Building2, Users, Trophy } from 'lucide-react';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { IMAGES, CONTACT } from '../constants/images';

export const Qualifications = () => {
  const qualifications = [
    { degree: 'MBBS', institution: 'Seth GS Medical College & KEM Hospital, Mumbai', board: 'MUHS', year: '2015' },
    { degree: 'MD – Radio Diagnosis', institution: 'Bombay Hospital Institute of Medical Sciences, Mumbai', board: 'MUHS', year: '2019' },
    { degree: 'DNB – Radio Diagnosis', institution: 'DNB Board, New Delhi', board: 'NBE', year: '2020' },
    { degree: 'FVIR (Fellowship, Vascular & IR)', institution: 'Seth GS Medical College & KEM Hospital, Mumbai', board: 'MUHS — With Distinction', year: '2021' },
    { degree: 'MNAMS', institution: 'National Academy of Medical Sciences', board: 'India', year: '2024' },
    { degree: 'CCNR', institution: 'Royal College of Radiologists / NHS Health Education England', board: 'UK', year: '2024' }
  ];

  const institutions = [
    {
      name: 'Seth GS Medical College & KEM Hospital, Mumbai',
      description: 'One of India\'s most respected public medical institutions — where Dr. Punit received both his foundational MBBS training (2015) and his specialist FVIR with Distinction (2021). The high-volume academic environment gave him exposure to pathology and procedures rare even among senior consultants.',
      icon: <Building2 className="w-8 h-8" />
    },
    {
      name: 'Bombay Hospital Institute of Medical Sciences, Mumbai',
      description: 'Dr. Punit completed his MD in Radio Diagnosis at Bombay Hospital Institute of Medical Sciences in 2019 — gaining advanced training in CT, MRI, ultrasound, Doppler, and nuclear medicine imaging.',
      icon: <Building2 className="w-8 h-8" />
    },
    {
      name: 'Tata Memorial Hospital, Mumbai',
      description: 'Senior Residency at India\'s premier cancer centre gave Dr. Punit specialised expertise in oncological imaging and cancer-directed vascular interventions — TACE, TARE, ablation, biliary interventions, and complex biopsies. This background distinguishes him from general radiologists treating cancer patients in Mumbai.',
      icon: <Building2 className="w-8 h-8" />
    }
  ];

  const memberships = [
    { org: 'ISVIR', full: 'Indian Society of Vascular & Interventional Radiology', description: 'India\'s foremost professional body for IR specialists.' },
    { org: 'CIRSE', full: 'Cardiovascular & Interventional Radiological Society of Europe', description: 'Premier European IR society — engagement with international guidelines and cutting-edge research.' },
    { org: 'MNAMS', full: 'National Academy of Medical Sciences', description: 'India\'s highest medical academy. Dr. Punit inducted in 2024.' }
  ];

  const faqs = [
    { q: 'What does FVIR mean and why does it matter for a Mumbai patient?', a: 'FVIR — Fellowship in Vascular & Interventional Radiology — is the gold standard IR credential in India. Dr. Punit holds this with distinction from KEM Hospital, Mumbai. Very few Interventional Radiologists in Mumbai hold this formal fellowship qualification.' },
    { q: 'What is special about training at Tata Memorial Hospital, Mumbai?', a: 'Tata Memorial Hospital is India\'s premier cancer centre in Mumbai. Training there provides high-volume exposure to oncological imaging and cancer-directed interventions — TACE, TARE, ablation, biliary stenting, and complex biopsies.' },
    { q: 'What is MNAMS?', a: 'MNAMS — Member, National Academy of Medical Sciences — is India\'s highest medical academy. Dr. Punit was inducted in 2024, recognising his contributions to clinical medicine and academic radiology.' },
    { q: 'Is Dr. Punit Mahajan recognised internationally?', a: 'Yes. He is a member of CIRSE (Europe\'s leading IR society) and holds the CCNR from the Royal College of Radiologists (UK) and NHS Health Education England — reflecting international-standard training.' }
  ];

  return (
    <>
      <SEO 
        title="Dr. Punit Mahajan — Qualifications, Training & Research | Interventional Radiologist Mumbai"
        description="MD, DNB, FVIR (with Distinction), CCNR, MNAMS. KEM Hospital, Tata Memorial & Bombay Hospital alumnus. ISVIR & CIRSE member. 15+ publications. Full credentials of Dr. Punit Mahajan."
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-950 to-slate-900 text-white py-20 relative overflow-hidden"><div className="absolute inset-0 pattern-hero opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Trophy className="w-16 h-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-text-reveal">
            Credentials, Training & Academic Profile
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto animate-text-reveal" style={{ animationDelay: "300ms" }}>
            Dr. Punit Mahajan — Interventional Radiologist, Mumbai
          </p>
        </div>
      </section>

      {/* Qualifications Table */}
      <section className="py-16 bg-cream-textured relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <GraduationCap className="w-12 h-12 text-amber-600 mx-auto mb-4" />
            <div className="gold-ornament mb-4">Credentials</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Qualifications at a Glance
            </h2>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid gap-4">
              {qualifications.map((q, idx) => (
                <Card key={idx} className="hover:shadow-lg transition-shadow border-l-4 border-amber-500 bg-white">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-blue-900 mb-1">{q.degree}</h3>
                        <p className="text-slate-700">{q.institution}</p>
                        <p className="text-sm text-slate-500">{q.board}</p>
                      </div>
                      <span className="inline-block bg-amber-100 text-amber-800 px-4 py-2 rounded-full font-semibold whitespace-nowrap">
                        {q.year}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Training Institutions */}
      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Building2 className="w-12 h-12 text-amber-600 mx-auto mb-4" />
            <div className="gold-ornament mb-4">Training</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Training Institutions in Mumbai & Beyond
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {institutions.map((inst, idx) => (
              <Card key={idx} className="hover:shadow-xl transition-all hover:-translate-y-1 card-gold-hover bg-white">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 mb-4">
                    {inst.icon}
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-3">{inst.name}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">{inst.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Academic Career */}
      <section className="py-16 bg-cream-pattern relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={IMAGES.doctor.scrubs1} 
                alt="Dr. Punit Mahajan - Academic Career"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div className="space-y-6">
              <Users className="w-12 h-12 text-amber-600" />
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                Academic & Teaching Career at KEM Hospital, Mumbai
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Following his FVIR, Dr. Punit served as an <span className="font-semibold text-blue-900">Assistant Professor in the Division of Interventional Radiology at KEM Hospital</span> for two years. He:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Award className="w-6 h-6 text-amber-600 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-slate-600">Performed and supervised the full spectrum of complex endovascular procedures independently</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-6 h-6 text-amber-600 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-slate-600">Trained postgraduate radiology residents in IR techniques and image interpretation</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-6 h-6 text-amber-600 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-slate-600">Contributed to academic teaching rounds, departmental case conferences, and CME sessions</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-6 h-6 text-amber-600 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-slate-600">Managed high-acuity emergency cases alongside surgical, hepatology, and oncology teams</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Memberships */}
      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Award className="w-12 h-12 text-amber-600 mx-auto mb-4" />
            <div className="gold-ornament mb-4">Memberships</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Professional Memberships
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {memberships.map((m, idx) => (
              <Card key={idx} className="text-center hover:shadow-xl transition-all card-gold-hover hover:-translate-y-1 bg-white">
                <CardContent className="p-8">
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-10 h-10 text-amber-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-blue-900 mb-2">{m.org}</h3>
                  <p className="text-sm font-semibold text-slate-700 mb-3">{m.full}</p>
                  <p className="text-slate-600 text-sm">{m.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Research & Publications */}
      <section className="py-16 bg-gradient-to-br from-blue-950 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <BookOpen className="w-16 h-16 text-amber-500 mx-auto" />
          <h2 className="text-3xl md:text-4xl font-bold">Research & Publications</h2>
          <div className="bg-blue-800/30 backdrop-blur-sm border border-amber-600/30 rounded-2xl p-8">
            <p className="text-6xl font-bold text-amber-500 mb-2">15+</p>
            <p className="text-xl text-slate-300">Peer-Reviewed Publications</p>
          </div>
          <p className="text-lg text-slate-300 leading-relaxed">
            Dr. Punit has over 15 peer-reviewed publications in national and international radiology journals, covering interventional oncology, vascular disease, hepatobiliary interventions, and minimally invasive procedures. He regularly attends CME workshops, training courses, and national and international conferences to stay at the forefront of IR advances in Mumbai.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-cream-pattern relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="border-l-4 border-amber-600">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">
            Expertise You Can Trust
          </h2>
          <p className="text-xl text-slate-300">
            Book a consultation with one of Mumbai's most qualified Interventional Radiologists
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${CONTACT.phone}`}><Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">Call {CONTACT.phone}</Button></a>
            <Link to="/contact"><Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">Book Consultation</Button></Link>
          </div>
        </div>
      </section>
    </>
  );
};
